import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import {FONT_PROXIMA_11, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, WHITE, GRAY_SEMIDARK} from 'ui-library/Colors';
import useDocumentScrollState from 'ui-library/useDocumentScrollState';

interface TableOfContentsProps {
  title: string; // The title of the table of contents
  contents: Array<{id: string; title: string}>; // The contents to display in the table of contents
  container: HTMLElement; // The container element which contains the elements to scroll to
  /**
   * This field represents the margin-top property of the experiment card
   * It is used in customScrollIntoView to adjust the scroll position so that there is
   * a top margin between the card and the top of the viewport.
   * Visually, it looks like this after clicking to scroll to the card:
   * ---container---
   *       |
   *     10px (or some contentsMarginTop value)
   *       |
   * ----element----
   */
  contentsMarginTop?: number;
  /**
   * This field represents the offset from the top of the viewport box to the threshold of
   * when elements become highlighted in the ToC.
   * It is used in findElementInViewport to adjust the top of the viewport box.
   * Visually, it looks like this:
   * ---container---
   *        |
   *       40px (or some viewportBoxOffset value)
   *        |
   * ----element----
   *
   */
  viewportBoxOffset?: number;
  emptyText?: string;
  getElementByScrollId?: (cid: string) => HTMLElement | null;
  customScrollIntoView?: (node: HTMLElement) => void;
  customFindElementInViewport?: (
    container: HTMLElement,
    getElementByScrollId: (cid: string) => HTMLElement | null,
    scrollIds: Array<string>,
    viewportBoxOffset: number
  ) => string | null;
}

/**
 * Finds the element in the viewport based on scroll positions. This function loops through all of the scroll IDs
 * of the elements in order from top to bottom and finds the element most in view. The logic allows us to
 * prioritize selecting the first element whose top is in the viewport, then the first element whose bottom is in the
 * viewport, and finally the element that takes up all the space in the viewport.
 *
 * @param container - The container element.
 * @param getElementByScrollId - A function that returns the element based on the scroll ID.
 * @param scrollIds - An array of scroll IDs.
 * @param viewPortBoxOffset - The offset between the top of the viewport box and the threshold of when elements become highlighted in the ToC.
 * @returns The ID of the element in the viewport, or null if no element is found.
 *
 * exported for testing
 */
export function findElementInViewport(
  container: HTMLElement,
  getElementByScrollId: (cid: string) => HTMLElement | null,
  scrollIds: Array<string>,
  viewportBoxOffset: number = 0
): string | null {
  const viewportBox = container.getBoundingClientRect();
  const viewportBoxTop = viewportBox.top + viewportBoxOffset;
  for (const id of scrollIds) {
    const el = getElementByScrollId(id);
    if (!el) {
      continue;
    }
    const element = el.getBoundingClientRect();
    if (element.bottom >= viewportBoxTop) {
      return id;
    }
  }
  return null;
}

export default function TableOfContents(props: TableOfContentsProps): JSX.Element {
  const getElementByScrollId = (scrollId: string): HTMLElement | null =>
    document.body.querySelector(`#${scrollId}`);

  const scrollIntoView = (node: HTMLElement): void => {
    const totalOffset = node.offsetTop - (props.contentsMarginTop ?? 0);
    props.container.scrollTop = totalOffset;
  };

  const customScrollIntoView = props.customScrollIntoView ?? scrollIntoView;
  const customFindElementInViewport = props.customFindElementInViewport ?? findElementInViewport;

  const {scrollPosition, jumpToScrollId} = useDocumentScrollState(
    props.contents.map((item) => item.id),
    getElementByScrollId,
    props.container,
    customScrollIntoView,
    (container, getElementByScrollId, scrollIds) =>
      customFindElementInViewport(container, getElementByScrollId, scrollIds, props.viewportBoxOffset ?? 0),
    true
  );

  return (
    <Col>
      <div className={css(styles.tocTitleText)}>{props.title}</div>
      <div>
        {props.contents.length === 0 ? (
          <div className={css(styles.noContentTextToC)}>{props.emptyText ?? 'No content to display'}</div>
        ) : (
          props.contents.map(({id, title}) => (
            <div key={id}>
              <Row>
                {scrollPosition && id === scrollPosition.scrollId && scrollPosition.auto && (
                  <div className={css(styles.circle)} />
                )}
                <Button
                  className={
                    scrollPosition && id === scrollPosition.scrollId && scrollPosition.auto
                      ? css(styles.selectedButton)
                      : css(styles.button)
                  }
                  buttonStyle={ButtonStyle.TRANSPARENT}
                  onClick={() => {
                    jumpToScrollId(id);
                  }}
                  size={ButtonSize.SMALL}
                  textClassName={
                    !scrollPosition ||
                    id !== scrollPosition.scrollId ||
                    (id === scrollPosition.scrollId && !scrollPosition.auto)
                      ? css(styles.unSelectedButtonText)
                      : undefined
                  }
                  text={title}
                />
              </Row>
            </div>
          ))
        )}
      </div>
    </Col>
  );
}

const styles = StyleSheet.create({
  tocTitleText: {
    backgroundColor: WHITE,
    color: GRAY_DARKER,
    ...FONT_PROXIMA_11,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    marginBottom: 5,
  },
  noContentTextToC: {
    color: Color.GRAY_500,
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    textAlign: 'left',
    padding: '16px 0px',
  },
  button: {
    marginBottom: -5,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    height: 'auto',
    marginLeft: -3,
  },
  unSelectedButtonText: {
    fontSize: 'small',
    fontWeight: 'normal',
    color: GRAY_SEMIDARK,
    textAlign: 'left',
  },
  selectedButton: {
    display: 'flex',
    fontSize: 'small',
    fontWeight: 'bolder',
    marginBottom: -5,
    marginLeft: -3,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    height: 'auto',
    textAlign: 'left',
  },
  circle: {
    display: 'flex',
    width: 5,
    height: 5,
    borderRadius: '50%',
    backgroundColor: Color.BLUE_450,
    marginTop: 11,
    position: 'absolute',
  },
});
